import { List, Text, Stack, Strong, Box } from 'braid-design-system';
import React, { type ComponentProps } from 'react';

import { TextLink } from 'src/components';
import { TRACKING_CODES } from 'src/shared/constant';
import { PRODUCT_TERMS, TERMS } from 'src/shared/links';

interface SubHeadingProps {
  children: string;
}

const LAYOUT_SPACING: ComponentProps<typeof Stack>['space'] = 'medium';

export const SubHeading = ({ children }: SubHeadingProps) => (
  <Text>
    <Strong>{children}</Strong>
  </Text>
);

export const PurchaseEligibility = () => (
  <Stack space={LAYOUT_SPACING}>
    <SubHeading>
      Eligibility to purchase a Guaranteed Hire Advertisement
    </SubHeading>

    <List space={LAYOUT_SPACING} type="number" start={1}>
      <Text>
        Private Advertisers are not eligible to purchase a Guaranteed Hire
        Advertisement on SEEK.
      </Text>

      <Text>
        By purchasing a Guaranteed Hire Advertisement on SEEK, you agree to
        display the identity of your business and consent to the use of your
        branding (including logo) by SEEK to promote the advertisement
      </Text>
    </List>
  </Stack>
);

export const RefundEligibility = () => (
  <Stack space={LAYOUT_SPACING}>
    <SubHeading>Eligibility to claim a refund</SubHeading>
    <List space={LAYOUT_SPACING} type="number" start={3}>
      <Stack space={LAYOUT_SPACING}>
        <Text>
          To be eligible to claim a refund on a Guaranteed Hire Advertisement:
        </Text>

        <List type="alpha">
          <Text>
            You must not, from the date your Guaranteed Hire Advertisement was
            first posted on the SEEK Website, until the conclusion of 4 weeks
            after expiry of your advertisement on the SEEK website (the{' '}
            <Strong>Hire Period</Strong>), have been able to hire a candidate
            for the advertised position.
          </Text>

          <Text>
            You must comply with{' '}
            <TextLink
              href={TERMS}
              target="_blank"
              tracking={TRACKING_CODES.advertising_term_of_use_link_clicked}
            >
              SEEK’s Advertising Terms of Use
            </TextLink>{' '}
            and{' '}
            <TextLink
              href={PRODUCT_TERMS}
              target="_blank"
              tracking={TRACKING_CODES.product_terms_conditions_link_clicked}
            >
              Product Terms and Conditions
            </TextLink>{' '}
            at all times;
          </Text>

          <Text>
            You must have used your best endeavours to locate a suitable
            candidate during the Hire Period; and
          </Text>

          <Text>
            You must have completed the Claim Form as detailed in clause 4
            below.
          </Text>
        </List>
      </Stack>
      <Stack space={LAYOUT_SPACING}>
        <Text>
          For the purposes of these Terms and Conditions, a candidate will be
          taken to have been hired during the Hire Period if they have accepted
          an offer for the position advertised irrespective of whether that:
        </Text>

        <List type="alpha">
          <Text>
            candidate has actually commenced employment during the Hire Period;
          </Text>

          <Text>candidate was hired via SEEK or an alternative source; or</Text>

          <Text>
            candidate&#39;s employement subsequently ceases for any reason.
          </Text>
        </List>
      </Stack>
    </List>
  </Stack>
);

export const MakingRefundClaim = () => (
  <Stack space={LAYOUT_SPACING}>
    <SubHeading>Making a claim for a refund</SubHeading>

    <List space={LAYOUT_SPACING} type="number" start={5}>
      <Stack space={LAYOUT_SPACING}>
        <Text>
          If you believe that you are eligible to claim a refund in respect of a
          SEEK Guaranteed Hire Advertisement you must:
        </Text>

        <List type="alpha">
          <Text>
            Email{' '}
            <Strong>
              <TextLink
                href="mailto:customerexperience@seek.com.au"
                target="_top"
                tracking={TRACKING_CODES.customer_service_email_clicked}
              >
                customerexperience@seek.com.au
              </TextLink>
            </Strong>{' '}
            within 10 business days of the conclusion of the Hire Period
            requesting a Guaranteed Hire Claim Form (Claim Form); and
          </Text>

          <Text>
            Return to SEEK a complete and correct Claim Form. A Claim Form will
            ask you to certify (amongst other things) that you have not hired a
            candidate for the advertised position during the Hire Period.
          </Text>
        </List>
      </Stack>

      <Text>
        When you submit a complete Claim Form, SEEK will provide you with a
        response regarding any refund entitlement within 20 business days from
        the date on which it received the Claim Form.
      </Text>

      <Stack space={LAYOUT_SPACING}>
        <Text>
          In making its decision SEEK reserves the right to request that you
          provide evidence necessary to substantiate that you have:
        </Text>

        <List type="alpha">
          <Text>
            not hired a candidate for the advertised position during the Hire
            Period; and
          </Text>

          <Text>
            used your best endeavours during the Hire Period to locate a
            suitable candidate.
          </Text>
        </List>
      </Stack>

      <Text>
        If SEEK is not reasonably satisfied with the evidence you provided in
        clause 7 above, it is not obligated to provide you with a refund.
      </Text>

      <Text>
        You warrant that all information provided by you in a Claim Form is true
        and correct.
      </Text>

      <Text>
        If your claim for a refund is successful, SEEK will refund the full
        price paid for the Guaranteed Hire Advertisement.
      </Text>

      <Text>
        Refunds will normally be paid to you by SEEK within 20 business days of
        you electing to accept a refund entitlement confirmed by SEEK and you
        providing any information required by SEEK to pay the refund.
      </Text>
    </List>
  </Stack>
);

export const General = () => (
  <Stack space={LAYOUT_SPACING}>
    <SubHeading>General</SubHeading>

    <List type="number" start={12}>
      <Text>
        SEEK may expire the Guaranteed Hire offering at any point in time
        subject to it honouring any Guaranteed Hire Advertisements purchased
        prior to the expiry of the offer.
      </Text>

      <Text>
        Other than in the circumstances specified above, Guaranteed Hire
        Advertisements are non-refundable.
      </Text>
    </List>
  </Stack>
);

export const MoneyBackPromise = () => (
  <Box>
    <Text>
      If you proceed with your purchase, you will be agreeing to our{' '}
      <TextLink
        href={TERMS}
        tracking={TRACKING_CODES.advertising_term_of_use_link_clicked}
      >
        Advertising Terms of Use
      </TextLink>{' '}
      and the terms and conditions specific to the Guaranteed Hire product,
      including the &ldquo;
      <TextLink
        href={PRODUCT_TERMS}
        tracking={TRACKING_CODES.product_terms_conditions_link_clicked}
      >
        Money Back Promise
      </TextLink>
      &rdquo;. Please see the section for “Australia / Rest of the world”.
    </Text>
  </Box>
);
